import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  model,
  OnDestroy,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HxBaseControl } from '../../base/hx-base-control';
import { sanitizeAndNormalizeUrl } from '../../utils/url';
import { HxCardContentComponent } from '../card/card-content.component';
import { HxCard, type HxCardAppearance } from '../card/card.component';
import { HxDivider } from '../divider/divider';
import { HxIcon } from '../icon/icon';
import { HxIconButton } from '../icon-button/icon-button';
import { LinkButton, type LinkButtonTarget } from '../link-button/link-button';
import { HxSwitch } from '../switch/switch';
import {
  DICTATION_ENABLED_CHANGED_EVENT,
  DictationState,
  HxDictationController,
  isDictationFeatureEnabled,
} from '../textarea/dictation';

/** Value model for the card-link control. Represents the mutable user-entered data within a card. */
export interface HxCardLinkValue {
  /** Whether a referral has been made */
  referralMade: boolean;
  /** Free-text notes about the referral */
  referralNotes: string;
}

/**
 * HxCardLink — Composite resource/referral card control.
 *
 * Composes `hx-card`, `hx-divider`, `hx-switch`, `hx-textarea`, `hx-label`, and `hx-link-button` into a standardized
 * card layout for documenting resource referrals.
 *
 * Implements `ControlValueAccessor` with `HxCardLinkValue` for reactive forms integration.
 *
 * @example
 *   ```html
 *   <hx-card-link
 *     title="Meals on Wheels"
 *     subtitle="Home-delivered meals for seniors and individuals with disabilities"
 *     linkUrl="https://www.mealsonwheels.org"
 *     [(ngModel)]="cardValue"
 *   />
 *   ```;
 *
 * @example
 *   ```html
 *   <!-- In a loop for multiple resources -->
 *   @for (resource of resources; track resource.id) {
 *     <hx-card-link
 *       [title]="resource.title"
 *       [subtitle]="resource.subtitle"
 *       [linkUrl]="resource.url"
 *       [formControl]="resource.control"
 *     />
 *   }
 *   ```;
 *
 * @see HxCard for card container
 * @see HxSwitch for toggle behavior
 * @see LinkButton for link CTA
 */
@Component({
  selector: 'hx-card-link',
  standalone: true,
  imports: [HxCard, HxCardContentComponent, HxDivider, HxSwitch, LinkButton, HxIcon, HxIconButton],
  templateUrl: './card-link.html',
  styleUrls: ['./card-link-tokens.css', './card-link.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'hx-card-link',
    '[class.hx-card-link--active]': 'referralMade()',
    '[class.hx-card-link--disabled]': 'disabledState()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: HxCardLink,
      multi: true,
    },
  ],
})
export class HxCardLink extends HxBaseControl implements ControlValueAccessor, AfterViewInit, OnDestroy {
  // ── Configuration Inputs ──────────────────────────────────────────

  /** Card title text (bold heading). */
  readonly cardTitle = input<string>('', { alias: 'title' });

  /** Card subtitle text (secondary description). */
  readonly cardSubtitle = input<string>('', { alias: 'subtitle' });

  /** Card visual appearance, passed through to inner `hx-card`. */
  readonly appearance = input<HxCardAppearance>('outlined');

  /** Label text for the "View Resource" link button. */
  readonly linkLabel = input<string>('View Resource');

  /** URL for the resource link. */
  readonly linkUrl = input<string>('');

  /** Target window/tab for the link button. */
  readonly linkTarget = input<LinkButtonTarget>('new-tab');

  /** Label for the referral toggle row. */
  readonly referralLabel = input<string>('Referral Made?');

  /** Label for the notes section. */
  readonly notesLabel = input<string>('Referral Notes');

  /** Placeholder text for the notes textarea. */
  readonly notesPlaceholder = input<string>('Notes');

  /** Whether notes are required when the referral toggle is ON. */
  readonly notesRequired = input<boolean>(false);

  // ── Mutable State (Model Signals) ─────────────────────────────────

  /** Two-way binding for the referral toggle state. */
  readonly referralMade = model<boolean>(false);

  /** Two-way binding for the referral notes text. */
  readonly referralNotes = model<string>('');

  // ── Outputs ───────────────────────────────────────────────────────

  /** Emits the URL when the link button is clicked. */
  readonly linkClicked = output<string>();

  /** Emits the URL when copy to clipboard succeeds. */
  readonly urlCopied = output<string>();

  // ── Internal State ────────────────────────────────────────────────

  /** Whether the copy icon shows a checkmark (after successful copy). */
  protected readonly copied = signal(false);

  /** Timer ID for resetting the copied state. */
  private copyTimerId: ReturnType<typeof setTimeout> | null = null;

  /** Reference to the notes textarea, used for dictation text insertion. */
  private readonly _notesTextarea = viewChild<ElementRef<HTMLTextAreaElement>>('notesTextarea');

  /**
   * Controls whether the dictation (speech-to-text) microphone icon may be shown on the notes textarea. Defaults to
   * `undefined`, meaning the component defers to the `dictationEnabled` local-storage feature flag.
   */
  readonly dictationEnabled = input<boolean | undefined>(undefined);

  /** Whether the current browser supports the Web Speech API used for dictation. */
  protected readonly _dictationSupported = HxDictationController.isSupported();

  /** Live-updating local-storage dictation feature flag (kept in sync via storage/custom events). */
  private readonly _dictationFeatureFlag = signal(isDictationFeatureEnabled());

  /** Whether the dictation microphone icon should be rendered. */
  protected readonly showDictation = computed(
    () => (this.dictationEnabled() ?? this._dictationFeatureFlag()) && this._dictationSupported
  );

  /** Current dictation lifecycle state: idle, listening, or error. */
  protected readonly dictationState = signal<DictationState>('idle');

  /** Accessible label/tooltip for the dictation button, reflecting the current state. */
  protected readonly dictationButtonLabel = computed(() =>
    this.dictationState() === 'listening' ? 'Stop dictation' : 'Start dictation'
  );

  private _dictationController: HxDictationController | null = null;

  /** Notes value captured the moment the current dictation session started. */
  private _dictationBaseValue = '';

  /** Finalized transcript text committed so far during the current dictation session. */
  private _dictationCommittedText = '';

  private readonly _refreshDictationFeatureFlag = (): void => {
    this._dictationFeatureFlag.set(isDictationFeatureEnabled());
    this._changeDetectorRef.markForCheck();
  };

  // ── Computed ──────────────────────────────────────────────────────

  /** Computed current form value. */
  protected readonly currentValue = computed<HxCardLinkValue>(() => ({
    referralMade: this.referralMade(),
    referralNotes: this.referralNotes(),
  }));

  /** Sanitized and normalized link URL for safe usage. */
  protected readonly sanitizedLinkUrl = computed(() => sanitizeAndNormalizeUrl(this.linkUrl()));

  // ── ControlValueAccessor ──────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: HxCardLinkValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  // Emit CVA changes whenever referralMade or referralNotes change
  private readonly syncEffect = effect(() => {
    const value = this.currentValue();
    this.onChange(value);
  });

  override writeValue(value: HxCardLinkValue | null): void {
    if (value) {
      this.referralMade.set(value.referralMade ?? false);
      this.referralNotes.set(value.referralNotes ?? '');
    } else {
      this.referralMade.set(false);
      this.referralNotes.set('');
    }
  }

  override registerOnChange(fn: (value: HxCardLinkValue) => void): void {
    this.onChange = fn;
  }

  override registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  override setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }

  // ── Event Handlers ────────────────────────────────────────────────

  /** Handles toggle change from the switch. */
  protected onReferralToggle(checked: boolean): void {
    this.referralMade.set(checked);
    this.onTouched();
  }

  /** Handles notes input changes. */
  protected onNotesChange(value: string): void {
    this.referralNotes.set(value);
    this.onTouched();

    // Manual edits while dictation is listening must not be clobbered by the next speech event, which
    // otherwise recomposes from the stale base captured when the session started.
    if (this.dictationState() === 'listening') {
      this._dictationBaseValue = value;
      this._dictationCommittedText = '';
    }
  }

  /** Pre-warms the microphone pipeline before the user actually clicks, reducing recognition startup latency. */
  protected onDictationButtonWarmUp(): void {
    HxDictationController.warmUp();
  }

  /** Handles clicks on the dictation microphone button: starts or stops the active recognition session. */
  protected onDictationButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabledState()) {
      return;
    }

    if (this.dictationState() === 'listening') {
      this._dictationController?.stop();
      return;
    }

    this._dictationBaseValue = this.referralNotes();
    this._dictationCommittedText = '';

    if (!this._dictationController) {
      this._dictationController = new HxDictationController({
        onInterimResult: (transcript) => this._previewDictatedNotes(transcript),
        onFinalResult: (transcript) => this._commitDictatedNotes(transcript),
        onStateChange: (state) => {
          this.dictationState.set(state);
          this._changeDetectorRef.markForCheck();
        },
        onError: () => {
          // Errors (permission denied/dismissed, no speech, network, unsupported, etc.) simply return the
          // control to idle/error state via onStateChange — existing notes are left completely untouched.
        },
      });
    }

    this._dictationController.start();
  }

  /** Builds `base + committed + interim`, skipping empty parts and joining with single spaces. */
  private _composeDictationValue(interim: string): string {
    return [this._dictationBaseValue.replace(/\s+$/, ''), this._dictationCommittedText, interim]
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .join(' ');
  }

  /** Live preview: shows in-progress (not-yet-final) speech immediately, without committing it. */
  private _previewDictatedNotes(interimTranscript: string): void {
    this._applyDictatedNotes(this._composeDictationValue(interimTranscript));
  }

  /** Commits finalized speech permanently so it is never overwritten/duplicated by later interim previews. */
  private _commitDictatedNotes(finalTranscript: string): void {
    const trimmed = finalTranscript.trim();
    if (!trimmed) {
      return;
    }
    this._dictationCommittedText = [this._dictationCommittedText, trimmed].filter(Boolean).join(' ');
    this._applyDictatedNotes(this._composeDictationValue('') + ' ');
  }

  /** Writes a dictation-driven value through the same model/CVA/touched pipeline as manual typing. */
  private _applyDictatedNotes(newValue: string): void {
    const element = this._notesTextarea()?.nativeElement;
    if (element) {
      element.value = newValue;
      element.setSelectionRange(newValue.length, newValue.length);
    }

    this.referralNotes.set(newValue);
    this.onTouched();
    this._changeDetectorRef.markForCheck();
  }

  ngAfterViewInit(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener(DICTATION_ENABLED_CHANGED_EVENT, this._refreshDictationFeatureFlag);
      window.addEventListener('storage', this._refreshDictationFeatureFlag);
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener(DICTATION_ENABLED_CHANGED_EVENT, this._refreshDictationFeatureFlag);
      window.removeEventListener('storage', this._refreshDictationFeatureFlag);
    }

    this._dictationController?.dispose();
    this._dictationController = null;
  }

  /** Handles link button click. */
  protected onLinkClick(event: MouseEvent): void {
    const url = this.sanitizedLinkUrl();
    if (url) {
      this.linkClicked.emit(url);
    }
  }

  /** Copies the link URL to the clipboard. */
  protected async copyUrl(): Promise<void> {
    const url = this.sanitizedLinkUrl();
    if (!url || this.disabledState()) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      this.copied.set(true);
      this.urlCopied.emit(url);

      // Reset after 2 seconds
      if (this.copyTimerId) {
        clearTimeout(this.copyTimerId);
      }
      this.copyTimerId = setTimeout(() => {
        this.copied.set(false);
        this.copyTimerId = null;
      }, 2000);
    } catch {
      // Graceful fallback — clipboard API may not be available
      this.fallbackCopyToClipboard(url);
    }
  }

  /** Fallback clipboard copy for older browsers. */
  private fallbackCopyToClipboard(text: string): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);

      this.copied.set(true);
      this.urlCopied.emit(text);

      if (this.copyTimerId) {
        clearTimeout(this.copyTimerId);
      }
      this.copyTimerId = setTimeout(() => {
        this.copied.set(false);
        this.copyTimerId = null;
      }, 2000);
    } catch {
      // Silent fail — nothing more we can do
    }
  }
}
